import { Metric } from "@/pages/graphs/group-graphs";
import React, { useEffect, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { ApartmentStats, Convert } from "@/models/responses";
import YearOverYearChart from "@/pages/graphs/year-over-year-chart";
import { SearchableSelect } from "@/lib/shadcn/components/searchable-select";
import { useActiveApartmentOptions } from "@/store/slices/apartments";
import { fetchOptions, getApiBasePath } from "@/hooks/fetchOptions";
import { apiUrl } from "@/layouts/apiUrl";

interface ApartmentYearOverYearChartProps {
    selectedMetric: {
        label: string;
        value: Metric;
        formatter: (value: number) => string;
    };
    startDate: Date;
    endDate: Date;
    selectedApartmentId: string;
}

const ApartmentYearOverYearChart: React.FC<ApartmentYearOverYearChartProps> = ({ selectedMetric, selectedApartmentId, startDate, endDate }) => {
    const apartmentOptions = useActiveApartmentOptions();
    const [stats, setStats] = useState<ApartmentStats[]>([]);
    const [months, setMonths] = useState<Date[]>([]);
    const [numberOfApartments, setNumberOfApartments] = useState<number>(0);
    const [selectedApartment, setSelectedApartment] = useState<string>(selectedApartmentId);
    const { user } = useAuth();

    useEffect(() => {
        if (!selectedApartment && apartmentOptions?.length > 0) {
            return;
        }
        const groupStatsUrl = `${apiUrl}${getApiBasePath()}/${user?.isAdmin ? "admin/" : ""}apartments/stats-by-month?apartments=${selectedApartment}&startDate=${startDate.toISOString().slice(0, 10)}&endDate=${endDate.toISOString().slice(0, 10)}`;
        fetch(groupStatsUrl, fetchOptions)
            .then((response) => response.text())
            .then((data) => {
                const ret = Convert.toGetMonthlyApartmentStatsResponse(data);
                setStats(ret.apartmentStats);
                setNumberOfApartments(ret.numberOfApartments);
                setMonths(ret.months);
            })
            .catch((error) => {
                console.error("Error fetching stats:", error);
                setStats([]);
            });
    }, [selectedApartment, startDate, endDate, apartmentOptions?.length, user?.isAdmin]); // Now only depends on the memoized dates object

    useEffect(() => {
        if (selectedApartment) {
            return;
        }

        const validOptions = apartmentOptions.filter((opt) => opt.value !== "0");

        if (validOptions.length > 0) {
            const randomApt = validOptions[Math.floor(Math.random() * validOptions.length)];
            if (randomApt && randomApt.value) {
                // eslint-disable-next-line react-hooks/set-state-in-effect
                setSelectedApartment(randomApt.value);
            }
        }
    }, [apartmentOptions, selectedApartment]);

    if (!selectedMetric || !apartmentOptions?.length) {
        return null;
    }

    return (
        <>
            <div className="flex flex-col sm:flex-row sm:justify-between mt-2 mb-4 sm:mt-8">
                <h2 className={"font-semibold mb-4 sm:mb-8"}>{selectedMetric.label} - Year over Year Comparison</h2>
                <SearchableSelect
                    variant={"volt"}
                    options={apartmentOptions.filter((option) => option.value !== "0")}
                    onChange={(newValue: string) => {
                        setSelectedApartment(newValue);
                    }}
                    selectedValue={selectedApartment}
                    placeholder={"Select Apartments"}
                    width={"w-auto"}
                />
            </div>
            <YearOverYearChart
                stats={stats
                    .filter((stat) => stat.apartmentId.toString() === selectedApartment)
                    .map((s, index) => ({
                        date: months[Math.floor(index / numberOfApartments)],
                        value: s[selectedMetric.value],
                    }))}
                valueFormatter={selectedMetric.formatter}
            />
        </>
    );
};

export default ApartmentYearOverYearChart;
